import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const EMPTY = { full_name: "", mobile: "", email: "", company_name: "", city: "", country: "", password: "", confirm_password: "", referral_code: "" };

export default function PartnerRegister() {
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [masked, setMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const register = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post("/partner/register", form);
      setMasked(r.data.mobile_masked || "");
      setDevOtp(r.data.dev_mobile_otp || "");
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/partner/verify-otp", { email: form.email, code: otp });
      setStep(3);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <section className="section-dark min-h-screen py-16 px-4" data-testid="partner-register-page">
      <div className="max-w-xl mx-auto">
        <p className="gold-kicker mb-2 text-center">PARTNER WITH 10XGLOBAL</p>
        <h1 className="font-display text-3xl md:text-4xl font-black text-slate-100 text-center" data-testid="partner-register-title">Become a Partner</h1>
        <p className="text-sm text-slate-400 text-center mt-3 mb-8">Refer clients. We verify, approve and activate them — you track everything live.</p>

        {step === 1 && (
          <form onSubmit={register} className="card-surface p-6 md:p-8 space-y-4" data-testid="partner-register-form">
            <div className="grid md:grid-cols-2 gap-4">
              <input value={form.full_name} onChange={set("full_name")} placeholder="Full Name *" data-testid="reg-full-name" className="input-dark text-sm" required />
              <input value={form.mobile} onChange={set("mobile")} placeholder="Mobile Number *" data-testid="reg-mobile" className="input-dark text-sm" required />
            </div>
            <input type="email" value={form.email} onChange={set("email")} placeholder="Email Address *" data-testid="reg-email" className="input-dark text-sm w-full" required />
            <input value={form.company_name} onChange={set("company_name")} placeholder="Company Name (optional)" data-testid="reg-company" className="input-dark text-sm w-full" />
            <div className="grid md:grid-cols-2 gap-4">
              <input value={form.city} onChange={set("city")} placeholder="City *" data-testid="reg-city" className="input-dark text-sm" required />
              <input value={form.country} onChange={set("country")} placeholder="Country *" data-testid="reg-country" className="input-dark text-sm" required />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <input type="password" value={form.password} onChange={set("password")} placeholder="Password (min 8) *" data-testid="reg-password" className="input-dark text-sm" required />
              <input type="password" value={form.confirm_password} onChange={set("confirm_password")} placeholder="Confirm Password *" data-testid="reg-confirm-password" className="input-dark text-sm" required />
            </div>
            <input value={form.referral_code} onChange={set("referral_code")} placeholder="Referral Code (optional)" data-testid="reg-referral-code" className="input-dark text-sm w-full" />
            {error && <p className="text-xs text-red-400" data-testid="register-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="register-submit-btn" className="btn-gold w-full !py-3.5 text-sm disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <><ShieldCheck size={16} /> REGISTER AS PARTNER</>}
            </button>
            <p className="text-[11px] text-slate-500 text-center">Already a partner? <Link to="/partner/login" className="text-gold hover:underline" data-testid="goto-login-link">Partner Login</Link></p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verify} className="card-surface p-6 md:p-8 space-y-4" data-testid="partner-otp-form">
            <p className="text-sm text-slate-300 text-center">Enter the 6-digit code sent to your mobile <span className="text-gold font-mono">{masked}</span></p>
            {devOtp && <p className="text-[11px] text-center text-slate-500 font-mono" data-testid="dev-otp-hint">DEV PREVIEW OTP: {devOtp}</p>}
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" data-testid="otp-input"
              className="input-dark text-center text-2xl tracking-[0.5em] font-mono w-full" required />
            {error && <p className="text-xs text-red-400" data-testid="otp-error">{error}</p>}
            <button type="submit" disabled={busy || otp.length !== 6} data-testid="otp-verify-btn" className="btn-gold w-full !py-3.5 text-sm disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : "VERIFY MOBILE"}
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="card-surface p-8 text-center" data-testid="register-success">
            <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold text-slate-100">Application Submitted</h2>
            <p className="text-sm text-slate-400 mt-3">Your partner application is now under admin review. You will be able to log in once approved.</p>
            <Link to="/partner/login" data-testid="success-login-btn" className="btn-gold inline-block mt-6 !px-8 !py-3 text-sm">GO TO PARTNER LOGIN</Link>
          </div>
        )}
      </div>
    </section>
  );
}
